import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/authApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface DraftInfo {
  id: number;
  version_number: number;
  status: string;
  original_filename: string | null;
  created_by: string | null;
  created_at: string | null;
  change_summary: string | null;
  comment_count: number;
}

interface DrugWithDrafts {
  drug_id: number;
  brand_name: string | null;
  generic_name: string | null;
  manufacturer: string | null;
  set_id: string;
  draft_count: number;
  latest_draft_at: string | null;
  drafts: DraftInfo[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ManageDraftsPage: React.FC = () => {
  const navigate = useNavigate();

  const [drugs, setDrugs] = useState<DrugWithDrafts[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedDrugs, setExpandedDrugs] = useState<Set<number>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ draft: DraftInfo; drugName: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ------- Fetch -------
  const fetchDrugs = useCallback(async (query?: string) => {
    setLoading(true);
    try {
      const data = await authApi.getDrugsWithDrafts(query || undefined);
      setDrugs(data.drugs);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load drafts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchDrugs();
  }, [fetchDrugs]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchDrugs(search);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, fetchDrugs]);

  // ------- Handlers -------
  const toggleExpand = (drugId: number) => {
    setExpandedDrugs((prev) => {
      const next = new Set(prev);
      if (next.has(drugId)) next.delete(drugId);
      else next.add(drugId);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await authApi.deleteVersion(deleteTarget.draft.id);
      // Refresh the list
      await fetchDrugs(search || undefined);
    } catch (err) {
      console.error('Failed to delete draft:', err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // ------- Render -------
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="text-slate-500 hover:text-slate-700 transition-colors"
            aria-label="Back to search"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-slate-900">Manage Drafts</h1>
          {!loading && (
            <span className="text-sm text-slate-500">
              {total} drug{total !== 1 ? 's' : ''} with drafts
            </span>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="max-w-5xl mx-auto px-4 pt-6 pb-2">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by drug name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-sm text-slate-500">Loading drafts...</span>
          </div>
        ) : drugs.length === 0 ? (
          <div className="text-center py-20">
            <svg className="mx-auto w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-3 text-sm text-slate-500">
              {search ? 'No drafts match your search.' : 'No drafts uploaded yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {drugs.map((drug) => {
              const isExpanded = expandedDrugs.has(drug.drug_id);
              const displayName = drug.brand_name || drug.generic_name || `Drug ${drug.drug_id}`;

              return (
                <div key={drug.drug_id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  {/* Drug header row */}
                  <button
                    onClick={() => toggleExpand(drug.drug_id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <svg
                        className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <div className="min-w-0">
                        <span className="font-medium text-slate-900 truncate block">{displayName}</span>
                        {drug.manufacturer && (
                          <span className="text-xs text-slate-500">{drug.manufacturer}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-xs text-slate-500">
                        {drug.draft_count} draft{drug.draft_count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-slate-400">
                        Latest: {formatDate(drug.latest_draft_at)}
                      </span>
                    </div>
                  </button>

                  {/* Expanded drafts */}
                  {isExpanded && (
                    <div className="border-t border-slate-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <th className="px-4 py-2 text-left font-medium">Version</th>
                            <th className="px-4 py-2 text-left font-medium">File</th>
                            <th className="px-4 py-2 text-left font-medium">Uploaded by</th>
                            <th className="px-4 py-2 text-left font-medium">Date</th>
                            <th className="px-4 py-2 text-right font-medium">Comments</th>
                            <th className="px-4 py-2 text-right font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drug.drafts.map((draft) => (
                            <tr key={draft.id} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-2.5 text-slate-900">v{draft.version_number}</td>
                              <td className="px-4 py-2.5 text-slate-600 max-w-[200px] truncate">
                                {draft.original_filename || '—'}
                              </td>
                              <td className="px-4 py-2.5 text-slate-600">
                                {draft.created_by || '—'}
                              </td>
                              <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                                {formatDate(draft.created_at)}
                              </td>
                              <td className="px-4 py-2.5 text-right text-slate-600">
                                {draft.comment_count}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget({ draft, drugName: displayName });
                                  }}
                                  className="text-red-400 hover:text-red-600 transition-colors p-1"
                                  title="Delete draft"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900">Delete Draft</h3>
            <p className="mt-2 text-sm text-slate-600">
              Delete version {deleteTarget.draft.version_number} of{' '}
              <span className="font-medium">{deleteTarget.drugName}</span>?
              {deleteTarget.draft.original_filename && (
                <span className="block mt-1 text-slate-400 text-xs truncate">
                  {deleteTarget.draft.original_filename}
                </span>
              )}
            </p>
            <p className="mt-2 text-xs text-red-500">This cannot be undone.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {deleting && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDraftsPage;
